import type { MetricOutcome } from '@brew/observability/metrics'

const CHANNEL_LINK_OUTCOMES = {
  linked: 'success',
  unconfirmed: 'rejected',
  missing: 'rejected',
  expired: 'rejected',
  wrong_org: 'rejected',
} as const satisfies Record<string, MetricOutcome>

/** A resolved link claim may still require confirmation or refuse persistence. */
export function classifyChannelLinkOutcome(value: unknown): MetricOutcome {
  if (value === null || typeof value !== 'object') {
    return 'unknown'
  }
  try {
    const status = (value as { status?: unknown }).status
    return typeof status === 'string' &&
      Object.hasOwn(CHANNEL_LINK_OUTCOMES, status)
      ? CHANNEL_LINK_OUTCOMES[status as keyof typeof CHANNEL_LINK_OUTCOMES]
      : 'unknown'
  } catch {
    return 'unknown'
  }
}

/** Existing app-error and campaign admission codes, never human error text. */
const PRODUCT_REJECTION_CODES = new Set([
  'NOT_FOUND',
  'NOT_AUTHENTICATED',
  'NOT_AUTHORIZED',
  'VALIDATION_ERROR',
  'CONFLICT',
  'INVALID_STATE',
  'LIMIT_REACHED',
  'ORG_BLOCKED',
  'EMPTY_SHELL',
  'CONSERVATION_FAILED',
  'INSUFFICIENT_ROLE',
  'DOMAIN_PURPOSE_NOT_ALLOWED',
  'NO_ELIGIBLE_RECIPIENTS',
  'SEND_QUOTA_EXCEEDED',
  'PROVIDER_QUOTA_EXHAUSTED',
  'EMAIL_AUDIT_CONFIRMATION_REQUIRED',
  'EMAIL_IMAGES_MISSING',
  'IDEMPOTENCY_CONFLICT',
  'CAMPAIGN_SEND_IN_PROGRESS',
])

/**
 * A resolved Promise can contain a failure. Only explicit known admission
 * refusals are rejected; untyped/server failures remain visible as failures.
 * Row/void mutation returns still count as successful resolution. These result
 * contracts expose no timeout/cancel code: thrown errors keep the shared
 * observeClientOperation classification instead of guessing from their text.
 */
export function classifyProductMutationResult(result: unknown): MetricOutcome {
  if (result === null || typeof result !== 'object') {
    return 'success'
  }
  try {
    const envelope = result as {
      success?: unknown
      ok?: unknown
      code?: unknown
    }
    const success = envelope.success
    const ok = envelope.ok
    if (
      (success !== undefined && typeof success !== 'boolean') ||
      (ok !== undefined && typeof ok !== 'boolean')
    ) {
      return 'failure'
    }
    if (success !== false && ok !== false) {
      return 'success'
    }
    if (success === true || ok === true) {
      return 'failure'
    }
    return typeof envelope.code === 'string' &&
      PRODUCT_REJECTION_CODES.has(envelope.code)
      ? 'rejected'
      : 'failure'
  } catch {
    return 'failure'
  }
}
